import React, { useEffect, useState } from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'antd';
import { ColumnType } from 'antd/lib/table';

import './index.less';

import {
  ProviderAvailableModelInfoModel,
  ProviderModel,
  ProviderModelOriginType,
  ProviderModelsEmbeddingItem,
  ProviderModelsLLMItem,
  ProviderModelsRerankItem,
  ProviderModelType,
  ProviderNames,
  ProviderSettingsOutput,
} from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import IbButton from '../../../../components/common/IbButton';
import IbIcon from '../../../../components/common/IbIcon';
import IbInput from '../../../../components/common/IbInput';
import IbModal from '../../../../components/common/IbModal';
import IbNumberInput from '../../../../components/common/IbNumberInput';
import IbPopover from '../../../../components/common/IbPopover';
import IbSelect from '../../../../components/common/IbSelect';
import IbTable from '../../../../components/common/IbTable';
import IbTypography from '../../../../components/common/IbTypography';
import Switcher from '../../../../components/common/Switcher';
import {
  API_KEY_MASKED_LENGTH,
  API_KEY_VISIBLE_LENGTH,
  DEFAULT_PROVIDER,
  MODEL_PROVIDER_NAMES_OPTIONS,
  MODEL_TYPE_NAMES,
  MODEL_TYPES_OPTIONS,
  PROVIDER_SETTINGS_DEFAULTS,
} from '../const';
import { ValidationErrors } from '../../../../types';
import { tryParseIntValue } from '../../../../utils/numberUtil';
import { mask, MaskingMode } from '../../../../utils/stringUtil';
import { CheckFieldValueFnMap, useValidationForm } from '../../../../utils/validationUtil';

enum AddModelMode {
  New = 'new',
  List = 'list',
}

const MAIN_CLASS_NAME = 'provider-edit-modal';
const FORM_ITEM_CLASS_NAME = `${MAIN_CLASS_NAME}__form-item`;
const SUBTITLE_CLASS_NAME = `${MAIN_CLASS_NAME}__subtitle`;
const ADD_POPOVER_CLASS_NAME = `${MAIN_CLASS_NAME}__add-popover`;
const ADD_POPOVER_ITEM_CLASS_NAME = `${MAIN_CLASS_NAME}__popover-item`;
const ADD_POPOVER_SWITCHER_CLASS_NAME = `${MAIN_CLASS_NAME}__popover-switcher`;
const TABLE_CLASS_NAME = `${MAIN_CLASS_NAME}__table`;
const ACTIONS_CELL_CLASS_NAME = `${MAIN_CLASS_NAME}__table-actions-cell`;

type ProviderModelsItemBase = ProviderModelsLLMItem | ProviderModelsEmbeddingItem | ProviderModelsRerankItem;

type ProviderForm = {
  title?: string | null;
  models?: ProviderModelsItemBase[];
};

const checkFieldValueFnMap: CheckFieldValueFnMap<ProviderForm> = {
  title: (value) => !!value?.length,
  models: () => true,
};

const getSettingsForType = (targetType: ProviderNames, currentSettings?: ProviderSettingsOutput) => {
  const defaults = PROVIDER_SETTINGS_DEFAULTS[targetType] || {};

  const commonValues = {
    provider_type: targetType,
    api_key: currentSettings?.api_key || '',
    models: currentSettings?.models || [],
  };

  const isSameType = currentSettings?.provider_type === targetType;
  const existingSpecificValues = isSameType ? currentSettings : {};

  return {
    ...defaults,
    ...existingSpecificValues,
    ...commonValues,
  };
};

export interface IProviderEditModalProps {
  visible: boolean;
  editProvider?: ProviderModel;
  onAdd: (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onUpdate: (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel: () => void;
}

const ProviderEditModal: React.FC<IProviderEditModalProps> = ({ visible, editProvider, onAdd, onUpdate, onCancel }) => {
  const { t } = useTranslation();

  const initialValues: ProviderForm = { title: editProvider?.title, models: editProvider?.settings.models };

  const {
    validate,
    validationState,
    reset,
    onFieldBlur,
    onFieldChange,
    onSubmitErrors,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ProviderForm>(initialValues, checkFieldValueFnMap);

  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<ProviderModel>(DEFAULT_PROVIDER);
  const [addAdditionalModelPopoverVisible, setAddAdditionalModelPopoverVisible] = useState(false);
  const [additionalProviderModel, setAdditionalProviderModel] = useState<ProviderModelsItemBase>();
  const [apiKeyEditing, setApiKeyEditing] = useState(false);
  const [availableModels, setAvailableModels] = useState<ProviderAvailableModelInfoModel[]>([]);
  const [loadingAvailableModels, setLoadingAvailableModels] = useState(false);
  const [isAvailableModelsLoadingError, setIsAvailableModelsLoadingError] = useState(false);
  const [addModelMode, setAddModelMode] = useState<AddModelMode>(AddModelMode.New);

  const isNewProvider = !editProvider?.provider_id;
  const isContextWindowInputVisible =
    additionalProviderModel?.type === ProviderModelType.Llm ||
    additionalProviderModel?.type === ProviderModelType.Embedding;
  const isAdditionalModelValid = !!additionalProviderModel?.name && !!additionalProviderModel?.type;
  const isAddModelFieldsDisabled = addModelMode === AddModelMode.List && isAvailableModelsLoadingError;

  const modalShowApiBase = 'api_base' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];
  const modalShowFolderId = 'folder_id' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];
  const modalShowScope = 'scope' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];

  const additionalModels =
    provider?.settings.models?.filter((m) => m.origin_type === ProviderModelOriginType.Custom) || [];

  const currentSettings = (provider?.settings as ProviderSettingsOutput) || {};

  const loadAvailableModels = async () => {
    if (!provider?.settings) {
      return;
    }

    setLoadingAvailableModels(true);
    setIsAvailableModelsLoadingError(false);

    try {
      const settings = currentSettings as {
        api_key?: string;
        api_base?: string;
        folder_id?: string;
        scope?: string;
      };
      const response = await defaultApi.searchProviderAvailableModelsGeneralProvidersModelsAvailableGet(
        provider.name,
        editProvider?.provider_id,
        // api_key is sent only when it was changed in the form
        // (otherwise the backend resolves it from the database by provider_id)
        settings.api_key,
        settings.api_base,
        settings.folder_id,
        settings.scope
      );
      setAvailableModels(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setAvailableModels([]);
      setIsAvailableModelsLoadingError(true);
    } finally {
      setLoadingAvailableModels(false);
    }
  };

  const onTitleChange = (value: string) => {
    setProvider({ ...provider, title: value } as ProviderModel);
    onFieldChange('title', value);
  };

  const onTitleBlur = () => onFieldBlur('title');

  const onNameChange = (value: SelectValue) => {
    const newType = value as ProviderNames;

    const newSettings = getSettingsForType(newType, provider?.settings);

    setProvider({
      ...provider,
      name: value,
      settings: newSettings,
    } as ProviderModel);
  };

  const onSettingsApiKeyChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, api_key: value } } as ProviderModel);

  const onStartEditApiKeyClick = () => {
    setApiKeyEditing(true);
    setProvider({ ...provider, settings: { ...provider?.settings, api_key: '' } } as ProviderModel);
  };

  const onCancelEditApiKeyClick = () => {
    setApiKeyEditing(false);
    setProvider({
      ...provider,
      settings: { ...provider?.settings, api_key: null },
    } as ProviderModel);
  };

  const onSettingsApiBaseChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, api_base: value } } as ProviderModel);

  const onSettingsFolderIdChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, folder_id: value } } as ProviderModel);

  const onSettingsScopeChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, scope: value } } as ProviderModel);

  const onAdditionalProviderModelNameChange = (value: SelectValue) => {
    const modelInfo = availableModels.find((model) => model.name === (value as string));
    setAdditionalProviderModel({
      ...additionalProviderModel,
      name: value as string,
      ...(modelInfo?.context_length != null ? { context_window: modelInfo.context_length } : {}),
    } as ProviderModelsItemBase);
  };

  const onAdditionalProviderModelTypeChange = (value: SelectValue) =>
    setAdditionalProviderModel({
      name: additionalProviderModel?.name,
      type: value,
      origin_type: additionalProviderModel?.origin_type,
    } as ProviderModelsItemBase);

  const onAdditionalProviderModelContextWindowChange = (value: number | string) =>
    setAdditionalProviderModel({
      ...additionalProviderModel,
      context_window: tryParseIntValue(value),
    } as ProviderModelsItemBase);

  const onModalSubmit = async (
    action: (provider: ProviderModel, onErrors: (errors: ValidationErrors) => void) => Promise<void>
  ) => {
    if (!validate()) return;
    setSaving(true);
    try {
      await action(provider, onSubmitErrors);
    } finally {
      setSaving(false);
    }
  };

  const onModalSaveButtonClick = () => onModalSubmit(onUpdate);
  const onModalAddButtonClick = () => onModalSubmit(onAdd);

  const onAddAdditionalModelButtonClick = () => {
    setAdditionalProviderModel({
      name: '',
      type: ProviderModelType.Llm,
      origin_type: ProviderModelOriginType.Custom,
    });
    setAddModelMode(AddModelMode.New);
    setAddAdditionalModelPopoverVisible(true);
    loadAvailableModels();
  };

  const onAddModelModeChange = (mode: AddModelMode) => {
    if (mode === addModelMode) return;
    setAddModelMode(mode);
    setAdditionalProviderModel({
      ...additionalProviderModel,
      name: '',
      context_window: undefined,
    } as ProviderModelsItemBase);
    if (mode === AddModelMode.List && isAvailableModelsLoadingError) {
      loadAvailableModels();
    }
  };

  const onAddAdditionalModelPopoverVisibleChange = (visible?: boolean) => {
    if (visible) return;

    setAddAdditionalModelPopoverVisible(visible || false);
  };

  const onAdditionalProviderModelAddButtonClick = () => {
    if (!additionalProviderModel) {
      return;
    }
    const newModels = [...(provider?.settings.models || []), additionalProviderModel];
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: newModels },
    } as ProviderModel);
    setAddAdditionalModelPopoverVisible(false);
    onFieldChange('models', newModels);
  };

  const onAdditionalModelsClearButtonClick = () => {
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: [] },
    } as ProviderModel);
    onFieldChange('models', []);
  };

  const onAdditionalModelDelete = (model: ProviderModelsItemBase) => () => {
    const newModels = provider?.settings.models?.filter((m) => m !== model);
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: newModels || [] },
    } as ProviderModel);
    onFieldChange('models', newModels);
  };

  const onVisiblePropChange = () => {
    if (!visible) return;

    if (editProvider) {
      const hydratedSettings = getSettingsForType(editProvider.name, editProvider.settings);
      setProvider({
        ...editProvider,
        settings: {
          ...(hydratedSettings as ProviderSettingsOutput),
          api_key: null,
        },
      });
      reset({ title: editProvider.title, models: editProvider.settings.models });
    }
    setAddAdditionalModelPopoverVisible(false);
    setApiKeyEditing(false);
    setAvailableModels([]);
    setIsAvailableModelsLoadingError(false);
    setAddModelMode(AddModelMode.New);
    setLoadingAvailableModels(false);
  };
  useEffect(onVisiblePropChange, [visible, editProvider]);

  const columns: ColumnType<ProviderModelsItemBase>[] = [
    {
      title: t('Providers: Name'),
      render: (_, record) => <IbTypography.Paragraph type="secondary">{record.name}</IbTypography.Paragraph>,
    },
    {
      title: t('Providers: Model type'),
      render: (_, record: ProviderModelsItemBase) => (
        <IbTypography.Paragraph type="secondary">{MODEL_TYPE_NAMES[record.type]}</IbTypography.Paragraph>
      ),
    },
    {
      title: t('Providers: Context window'),
      render: (_, record: ProviderModelsItemBase) =>
        record.type === ProviderModelType.Llm || record.type === ProviderModelType.Embedding ? (
          <IbTypography.Paragraph type="secondary">
            {(record as ProviderModelsLLMItem).context_window}
          </IbTypography.Paragraph>
        ) : null,
    },
    {
      className: ACTIONS_CELL_CLASS_NAME,
      title: (
        <IbButton type="link" onClick={onAdditionalModelsClearButtonClick}>
          {t('Providers: Clear list')}
        </IbButton>
      ),
      render: (_, record) => (
        <IbButton icon={<IbIcon iconName="delete" />} type="icon" onClick={onAdditionalModelDelete(record)} />
      ),
      dataIndex: 'actions',
    },
  ];

  const availableModelSelector = (
    <>
      <IbTypography.Paragraph type="secondary">{t('Providers: Add model: Field: From list')}</IbTypography.Paragraph>
      <IbSelect
        filterOption
        showSearch
        disabled={isAddModelFieldsDisabled}
        loading={loadingAvailableModels}
        notFoundContent={loadingAvailableModels || !availableModels.length ? <></> : undefined}
        options={availableModels.map((m) => ({ value: m.name, label: m.name, title: m.name }))}
        placeholder={t('Providers: Model name placeholder')}
        value={additionalProviderModel?.name || undefined}
        onChange={onAdditionalProviderModelNameChange}
      />
      {isAvailableModelsLoadingError && (
        <IbTypography.Paragraph error type="descriptor">
          {t('Providers: Models load error')}
        </IbTypography.Paragraph>
      )}
      {!loadingAvailableModels && !isAvailableModelsLoadingError && !availableModels.length && (
        <IbTypography.Paragraph type="descriptor">{t('Providers: Models not supported')}</IbTypography.Paragraph>
      )}
    </>
  );

  const customModelInput = (
    <>
      <IbTypography.Paragraph type="secondary">{t('Providers: Add model: Field: New')}</IbTypography.Paragraph>
      <IbInput value={additionalProviderModel?.name} onChange={onAdditionalProviderModelNameChange} />
    </>
  );

  const addAdditionalModelPopoverContent = (
    <>
      <Switcher
        className={ADD_POPOVER_SWITCHER_CLASS_NAME}
        options={[
          { value: AddModelMode.New, label: t('Providers: Add model: Tab: New') },
          { value: AddModelMode.List, label: t('Providers: Add model: Tab: From list') },
        ]}
        value={addModelMode}
        onChange={(value) => onAddModelModeChange(value as AddModelMode)}
      />
      <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
        {addModelMode === AddModelMode.List ? availableModelSelector : customModelInput}
      </div>
      <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{t('Providers: Model type')}</IbTypography.Paragraph>
        <IbSelect
          disabled={isAddModelFieldsDisabled}
          options={MODEL_TYPES_OPTIONS}
          value={additionalProviderModel?.type}
          onChange={onAdditionalProviderModelTypeChange}
        />
      </div>
      {isContextWindowInputVisible && (
        <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('Providers: Context window')}</IbTypography.Paragraph>
          <IbNumberInput
            disabled={isAddModelFieldsDisabled}
            min={0}
            precision={0}
            value={(additionalProviderModel as ProviderModelsLLMItem)?.context_window ?? undefined}
            onChange={onAdditionalProviderModelContextWindowChange}
          />
        </div>
      )}
      <IbButton
        disabled={!isAdditionalModelValid || isAddModelFieldsDisabled}
        type="fill"
        onClick={onAdditionalProviderModelAddButtonClick}
      >
        {t('Providers: Add')}
      </IbButton>
    </>
  );

  const modalFooter = (
    <>
      <IbButton disabled={saving} onClick={isNewProvider ? onModalAddButtonClick : onModalSaveButtonClick}>
        {t(isNewProvider ? 'Providers: Add' : 'Providers: Save')}
      </IbButton>
      <IbButton type="secondary" onClick={onCancel}>
        {t('Providers: Cancel')}
      </IbButton>
    </>
  );

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={modalFooter}
      title={isNewProvider ? t('Providers: Add provider') : editProvider?.title}
      visible={visible}
      width="50%"
      onCancel={onCancel}
    >
      <div className={SUBTITLE_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Providers: Parameters')}
        </IbTypography.Paragraph>
      </div>
      <Row>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: Name')}</IbTypography.Paragraph>
            <IbInput
              status={getShowError(validationState.title) ? 'error' : 'default'}
              value={provider?.title}
              onBlur={onTitleBlur}
              onChange={onTitleChange}
            />
            {getShowError(validationState.title) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.title, t('Providers: Name: Error'))}
              </IbTypography.Paragraph>
            )}
          </div>
        </Col>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: Provider')}</IbTypography.Paragraph>
            <IbSelect options={MODEL_PROVIDER_NAMES_OPTIONS} value={provider?.name} onChange={onNameChange} />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: API Key')}</IbTypography.Paragraph>
            <IbInput
              disabled={!isNewProvider && !apiKeyEditing}
              suffix={
                !isNewProvider && (editProvider?.settings.api_key || apiKeyEditing) ? (
                  <IbButton
                    icon={<IbIcon iconName={apiKeyEditing ? 'close' : 'edit'} />}
                    type="icon"
                    onClick={apiKeyEditing ? onCancelEditApiKeyClick : onStartEditApiKeyClick}
                  />
                ) : undefined
              }
              value={
                !isNewProvider && !apiKeyEditing
                  ? mask(
                      editProvider?.settings.api_key,
                      MaskingMode.BeginEnd,
                      API_KEY_MASKED_LENGTH,
                      API_KEY_VISIBLE_LENGTH
                    )
                  : provider?.settings.api_key
              }
              onChange={onSettingsApiKeyChange}
            />
          </div>
        </Col>
        <Col>
          {modalShowApiBase && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Base URL')}</IbTypography.Paragraph>
              <IbInput value={(currentSettings as { api_base: string }).api_base} onChange={onSettingsApiBaseChange} />
            </div>
          )}
          {modalShowFolderId && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Folder ID')}</IbTypography.Paragraph>
              <IbInput
                value={(currentSettings as { folder_id: string }).folder_id}
                onChange={onSettingsFolderIdChange}
              />
            </div>
          )}
          {modalShowScope && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Scope')}</IbTypography.Paragraph>
              <IbInput value={(currentSettings as { scope: string }).scope} onChange={onSettingsScopeChange} />
            </div>
          )}
        </Col>
      </Row>
      <div className={SUBTITLE_CLASS_NAME}>
        <IbTypography.Paragraph strong error={getShowError(validationState.models)} type="secondary">
          {t('Providers: Additional models')}
        </IbTypography.Paragraph>
        <IbPopover
          className={ADD_POPOVER_CLASS_NAME}
          content={addAdditionalModelPopoverContent}
          placement="rightTop"
          trigger="click"
          visible={addAdditionalModelPopoverVisible}
          onVisibleChange={onAddAdditionalModelPopoverVisibleChange}
        >
          <IbButton icon={<IbIcon iconName="add-one" />} type="link" onClick={onAddAdditionalModelButtonClick} />
        </IbPopover>
      </div>
      {additionalModels.length ? (
        <IbTable className={TABLE_CLASS_NAME} columns={columns} dataSource={additionalModels} />
      ) : null}
      {getShowError(validationState.models) && (
        <IbTypography.Paragraph error type="descriptor">
          {getErrorMessage(validationState.models, t('Providers: Additional Models: Error'))}
        </IbTypography.Paragraph>
      )}
    </IbModal>
  );
};

export default ProviderEditModal;
